import React, { useEffect, useState } from 'react';
import { usersAPI, rolesAPI } from '../services/api';
import { User, Role } from '../types';
import { useAuth } from '../contexts/AuthContext';
import './Users.css';

const Users: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [roles, setRoles] = useState<Role[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState<User | null>(null);
  const [formData, setFormData] = useState({
    phoneNumber: '',
    password: '',
    firstName: '',
    lastName: '',
    roleIds: [] as number[],
  });
  const [showDeleted, setShowDeleted] = useState(false);
  const { isAdmin } = useAuth();

  useEffect(() => {
    fetchUsers();
    fetchRoles();
  }, [showDeleted]);

  const fetchUsers = async () => {
    try {
      const data = await usersAPI.getAll({ withDeleted: showDeleted });
      setUsers(data);
    } catch (error) {
      console.error('Failed to fetch users:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchRoles = async () => {
    try {
      const data = await rolesAPI.getAll();
      setRoles(data);
    } catch (error) {
      console.error('Failed to fetch roles:', error);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (editing) {
        // For update, only send password if it's provided
        const updateData: any = {
          phoneNumber: formData.phoneNumber,
          firstName: formData.firstName,
          lastName: formData.lastName || undefined,
          roleIds: formData.roleIds,
        };
        if (formData.password) {
          updateData.password = formData.password;
        }
        await usersAPI.update(editing.id, updateData);
      } else {
        await usersAPI.create({
          phoneNumber: formData.phoneNumber,
          password: formData.password,
          firstName: formData.firstName,
          lastName: formData.lastName || undefined,
          roleIds: formData.roleIds,
        });
      }
      setShowForm(false);
      setEditing(null);
      setFormData({
        phoneNumber: '',
        password: '',
        firstName: '',
        lastName: '',
        roleIds: [],
      });
      fetchUsers();
    } catch (error: any) {
      alert(error.response?.data?.message || 'Ошибка при сохранении');
    }
  };

  const handleEdit = (user: User) => {
    setEditing(user);
    setFormData({
      phoneNumber: user.phoneNumber,
      password: '', // Don't pre-fill password
      firstName: user.firstName,
      lastName: user.lastName || '',
      roleIds: user.roles?.map((r) => r.id) || [],
    });
    setShowForm(true);
  };

  const handleDelete = async (user: User) => {
    if (!window.confirm(`Удалить пользователя "${user.firstName}" (${user.phoneNumber})?`)) return;
    try {
      await usersAPI.delete(user.id);
      fetchUsers();
    } catch (error: any) {
      alert(error.response?.data?.message || 'Ошибка при удалении');
    }
  };

  const handleRestore = async (user: User) => {
    if (!window.confirm(`Восстановить пользователя "${user.firstName}" (${user.phoneNumber})?`)) return;
    try {
      await usersAPI.restore(user.id);
      fetchUsers();
    } catch (error: any) {
      alert(error.response?.data?.message || 'Ошибка при восстановлении');
    }
  };

  const handleRoleToggle = (roleId: number) => {
    setFormData((prev) => {
      const roleIds = prev.roleIds.includes(roleId)
        ? prev.roleIds.filter((id) => id !== roleId)
        : [...prev.roleIds, roleId];
      return { ...prev, roleIds };
    });
  };

  if (loading) {
    return <div>Загрузка...</div>;
  }

  return (
    <div className="users">
      <div className="page-header">
        <h1>Пользователи</h1>
        <div className="page-header-actions">
          {isAdmin() && (
            <button
              className={`btn-toggle-deleted ${showDeleted ? 'active' : ''}`}
              onClick={() => setShowDeleted(!showDeleted)}
            >
              🗑 {showDeleted ? 'Скрыть удалённые' : 'Показать удалённые'}
            </button>
          )}
          {isAdmin() && (
            <button onClick={() => setShowForm(!showForm)} className="btn-primary">
              {showForm ? 'Отмена' : '+ Добавить пользователя'}
            </button>
          )}
        </div>
      </div>

      {showForm && isAdmin() && (
        <div className="form-card">
          <h2>{editing ? 'Редактировать пользователя' : 'Новый пользователь'}</h2>
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label>Номер телефона *</label>
              <input
                type="tel"
                value={formData.phoneNumber}
                onChange={(e) => setFormData({ ...formData, phoneNumber: e.target.value })}
                required
                placeholder="+996554920072"
              />
            </div>
            <div className="form-group">
              <label>Пароль {editing ? '(оставьте пустым, чтобы не менять)' : '*'}</label>
              <input
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                required={!editing}
                minLength={6}
                placeholder={editing ? 'Оставьте пустым, чтобы не менять' : 'Минимум 6 символов'}
              />
            </div>
            <div className="form-group">
              <label>Имя *</label>
              <input
                type="text"
                value={formData.firstName}
                onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                required
              />
            </div>
            <div className="form-group">
              <label>Фамилия</label>
              <input
                type="text"
                value={formData.lastName}
                onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
              />
            </div>
            <div className="form-group">
              <label>Роли *</label>
              <div className="roles-checkboxes">
                {roles.map((role) => (
                  <label key={role.id} className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={formData.roleIds.includes(role.id)}
                      onChange={() => handleRoleToggle(role.id)}
                    />
                    <span>{role.name}</span>
                  </label>
                ))}
              </div>
              {formData.roleIds.length === 0 && (
                <span className="error-text">Выберите хотя бы одну роль</span>
              )}
            </div>
            <button type="submit" className="btn-primary" disabled={formData.roleIds.length === 0}>
              {editing ? 'Сохранить' : 'Создать'}
            </button>
          </form>
        </div>
      )}

      {/* Desktop Users Table */}
      <div className="table-container users-desktop-table">
        <table>
          <thead>
            <tr>
              <th>ID</th>
              <th>Телефон</th>
              <th>Имя</th>
              <th>Фамилия</th>
              <th>Роли</th>
              <th>Активен</th>
              {isAdmin() && <th>Действия</th>}
            </tr>
          </thead>
          <tbody>
            {users.map((user) => (
              <tr key={user.id} className={user.deletedAt ? 'row-deleted' : ''}>
                <td>{user.id}</td>
                <td>{user.phoneNumber}</td>
                <td>{user.firstName}</td>
                <td>{user.lastName || '-'}</td>
                <td>{user.roles?.map((role) => role.name).join(', ') || '-'}</td>
                <td>{user.isActive ? 'Да' : 'Нет'}</td>
                {isAdmin() && (
                  <td>
                    <div className="action-buttons-cell">
                      {!user.deletedAt && (
                        <button onClick={() => handleEdit(user)} className="btn-edit">Изменить</button>
                      )}
                      {user.deletedAt ? (
                        <button onClick={() => handleRestore(user)} className="btn-restore">Восстановить</button>
                      ) : (
                        <button onClick={() => handleDelete(user)} className="btn-delete">Удалить</button>
                      )}
                    </div>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile Users Cards */}
      <div className="users-mobile-list">
        {users.length === 0 ? (
          <div className="mobile-empty-card">Пользователи не найдены</div>
        ) : (
          users.map((user) => (
            <div key={user.id} className={`mobile-user-card ${user.deletedAt ? 'card-deleted' : ''}`}>
              <div className="mobile-card-head">
                <div className="mobile-user-title-col">
                  <div className="mobile-user-avatar">
                    {user.firstName ? user.firstName.charAt(0).toUpperCase() : '👤'}
                  </div>
                  <div>
                    <span className="mobile-user-name">{user.firstName} {user.lastName || ''}</span>
                    <span className="mobile-user-phone">{user.phoneNumber}</span>
                  </div>
                </div>
                <span className={`mobile-user-status ${user.isActive ? 'status-active' : 'status-inactive'}`}>
                  {user.isActive ? 'Активен' : 'Неактивен'}
                </span>
              </div>

              <div className="mobile-card-details">
                <div className="mobile-detail-row">
                  <span className="mobile-detail-label">Роли:</span>
                  <div className="mobile-roles-wrap">
                    {user.roles && user.roles.length > 0 ? (
                      user.roles.map(r => (
                        <span key={r.id} className="mobile-role-pill">{r.name}</span>
                      ))
                    ) : (
                      <span className="mobile-detail-val">—</span>
                    )}
                  </div>
                </div>
              </div>

              {isAdmin() && (
                <div className="mobile-card-footer-actions">
                  {!user.deletedAt && (
                    <button onClick={() => handleEdit(user)} className="btn-edit">Изменить</button>
                  )}
                  {user.deletedAt ? (
                    <button onClick={() => handleRestore(user)} className="btn-restore">Восстановить</button>
                  ) : (
                    <button onClick={() => handleDelete(user)} className="btn-delete">Удалить</button>
                  )}
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default Users;
